import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useNavGuard } from "../context/NavGuardContext";

const MESSAGE_TYPES = [
  { value: "technical", label: "Falla técnica de la plataforma" },
  { value: "privacy", label: "Problema de privacidad" },
  { value: "correction", label: "Solicitud de corrección" },
  { value: "general", label: "Duda general" },
  { value: "other", label: "Otro" },
];

const EMPTY = {
  name: "", contact: "", message_type: "", message: "", related_case_code: "",
  not_emergency_ack: false, website: "",
};

export default function Contact() {
  const navigate = useNavigate();
  const { setGuard, attempt } = useNavGuard();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const dirty = !sent && JSON.stringify(form) !== JSON.stringify(EMPTY);

  useEffect(() => {
    setGuard({ dirty, kind: "contact", onDiscard: () => setForm(EMPTY) });
    return () => setGuard(null);
  }, [dirty, setGuard]);

  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message_type) { toast.error("Selecciona el tipo de mensaje."); return; }
    if (form.message.trim().length < 5) { toast.error("Escribe un mensaje (mínimo 5 caracteres)."); return; }
    if (!form.not_emergency_ack) { toast.error("Debes confirmar la nota sobre emergencias."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      toast.success(data?.message || "Mensaje recibido.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo enviar el mensaje. Intenta de nuevo.");
    } finally { setBusy(false); }
  };

  const sendAnother = () => { setForm(EMPTY); setSent(false); };

  const fieldCls = "w-full h-11 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#003893]/30";

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <button onClick={() => attempt(() => navigate("/"))} data-testid="contact-back-btn"
        className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#003893]">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A]">Contáctanos</h1>
        <p className="mt-2 text-sm sm:text-base text-[#475569] leading-relaxed">
          Usa este formulario para reportar fallas de la plataforma, problemas de privacidad, solicitudes de corrección o dudas generales. Para gestionar un caso específico, usa siempre los formularios protegidos dentro de cada ficha.
        </p>
      </div>

      {sent ? (
        <div className="space-y-4" data-testid="contact-success">
          <div className="flex items-start gap-3 rounded-xl bg-[#ECFDF3] border border-[#A7F3D0] p-5">
            <CheckCircle2 className="w-6 h-6 text-[#047857] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[#065F46]">Mensaje recibido.</p>
              <p className="text-sm text-[#047857] leading-relaxed mt-0.5">El equipo de Reencuentro lo revisará lo antes posible.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate("/")} data-testid="contact-go-home"
              className="h-12 px-6 rounded-xl bg-[#003893] text-white font-semibold hover:bg-[#002a6e] transition-colors">Volver al inicio</button>
            <button onClick={sendAnother} data-testid="contact-send-another"
              className="h-12 px-6 rounded-xl bg-white border border-[#E5E7EB] text-[#0F172A] font-semibold hover:bg-[#F8FAFC] transition-colors">Enviar otro mensaje</button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3 bg-white border border-[#E5E7EB] rounded-2xl p-5 sm:p-7" data-testid="contact-form">
          {/* Honeypot (hidden from users) */}
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website}
            onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" data-testid="contact-honeypot" />

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold text-[#475569]">Nombre (opcional)</span>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120}
                className={`mt-1 ${fieldCls}`} data-testid="contact-name" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#475569]">Email o teléfono (opcional, recomendado)</span>
              <input value={form.contact} onChange={(e) => set("contact", e.target.value)} maxLength={200}
                className={`mt-1 ${fieldCls}`} data-testid="contact-contact" />
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold text-[#475569]">Tipo de mensaje <span className="text-[#CE1126]">*</span></span>
              <select value={form.message_type} onChange={(e) => set("message_type", e.target.value)}
                className={`mt-1 ${fieldCls}`} data-testid="contact-type" required>
                <option value="">Selecciona…</option>
                {MESSAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#475569]">Código de caso relacionado (opcional)</span>
              <input value={form.related_case_code} onChange={(e) => set("related_case_code", e.target.value)} maxLength={20}
                placeholder="RE-XXXXXX" className={`mt-1 ${fieldCls} font-mono`} data-testid="contact-case-code" />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-[#475569]">Mensaje <span className="text-[#CE1126]">*</span></span>
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} maxLength={5000}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E7EB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#003893]/30"
              data-testid="contact-message" required />
          </label>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={form.not_emergency_ack} onChange={(e) => set("not_emergency_ack", e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#003893]" data-testid="contact-ack" />
            <span className="text-xs text-[#475569] leading-relaxed">
              Entiendo que este formulario no es un canal de emergencia ni sustituye a autoridades, servicios médicos o de rescate.
            </span>
          </label>

          <p className="text-xs text-[#94A3B8]">Por privacidad, no incluyas C.I., dirección exacta ni datos médicos. No se admiten archivos adjuntos.</p>

          <button type="submit" disabled={busy} data-testid="contact-submit"
            className="flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-[#003893] text-white font-semibold hover:bg-[#002a6e] transition-colors disabled:opacity-50">
            <Send className="w-4 h-4" /> {busy ? "Enviando…" : "Enviar mensaje"}
          </button>
        </form>
      )}
    </div>
  );
}
